import type { CardTitle, EffectId, PackId } from './ids';
import type { Lore } from './lore';

/**
 * 卡牌的**行为特征**。
 *
 * ⚠️ 这是整个可扩展设计的核心：
 * **核心逻辑只认这些特征，绝不认牌名。**
 *
 * 比如「抽到就死」这个规则，核心看的是 `traits.lethal`，
 * 而不是 `title === 'vanilla:Exception'`。于是扩展包可以塞进任何
 * 形状怪异的新致命牌（内爆猫、僵尸猫……），核心一行都不用改。
 */
export interface CardTraits {
  /**
   * 抽到它 → 触发致命结算。
   * `defusable: false` 表示它**不能**被拆除卡救（内爆猫就是这样）。
   */
  readonly lethal?: { readonly defusable: boolean };

  /** 致命结算时，手上持有它就能救命（拆除卡之类） */
  readonly rescue?: boolean;

  /** 可以在**别人的回合**打出（休想之类） */
  readonly interrupt?: boolean;

  /** 单独打出**没有任何效果**，只能用于连击（无效牌） */
  readonly powerless?: boolean;

  /** 能否在出牌阶段主动打出。省略 = 可以 */
  readonly playable?: boolean;

  /**
   * **这张牌永远不会待在某人的手里**（区域硬约束）。
   *
   * 记牌器用它把这类牌的"未知"份额从手牌里排除掉。例如异常牌：抽到的瞬间就
   * 触发濒死、结算完会进弃牌堆或被插回抽牌堆 —— 活人手里不可能有它。
   * 省略 = 没有这个约束（一般的牌）。
   */
  readonly neverInHand?: boolean;

  /**
   * 主动打出时**必须指定一个目标玩家**（Await 求值就是这样）。
   *
   * 有了这个声明，reducer 才能在校验阶段拦住"忘记选目标"的出牌，
   * 而不是让效果函数拿到 null 之后静默什么都不做。
   */
  readonly needsTarget?: boolean;
}

/** 一张卡的静态定义。这是**内容**，不是状态 —— 一局游戏里它不会变。 */
export interface CardDef {
  /** 命名空间化的牌名，例如 `vanilla:Exception` */
  readonly id: CardTitle;

  /** 它属于哪个数据包 */
  readonly pack: PackId;

  /** 包内局部名，例如 `Exception` / `Powerless1` */
  readonly local: string;

  /** 中文显示名。**只有 UI 能读它，逻辑一律不许读** */
  readonly name: string;

  /** 英文显示名。省略时回退中文（见 `cardText.ts`） */
  readonly nameEn?: string;

  readonly description: string;

  /** 印在牌面上的英文短文案。省略时回退中文（见 `cardText.ts`） */
  readonly descriptionEn?: string;

  /**
   * **详细效果说明**（比 `description` 长），悬停时显示在牌上方/下方那块说明里。
   *
   * 为什么要有它、而不是把 `description` 写长：`description` 是**印在牌面上**的，
   * 一行只有十来个字的位置；而悬停说明是弹出来的一块面板，能写清楚
   * "什么时候能打、打出去发生什么、有什么代价、什么时候不该打"。
   * 两张牌的信息量差着一个数量级，所以是**两个字段**，不是一个字段的两种用法。
   *
   * ⚠️ 它现在是**结构化的**（按层面分段 + 极简富文本），见 `core/lore.ts`。
   *    旧的一坨字符串已经拆开；牌引用按 id 存，改名/换语言会自动跟着变。
   *
   * ⚠️ 和 `name` 一样：**只有 UI 能读它，逻辑一律不许读**。
   */
  readonly lore: Lore;

  /** 悬停说明的英文详细效果。省略时回退中文（见 `cardText.ts`） */
  readonly loreEn?: Lore;

  readonly traits: CardTraits;

  /**
   * 主动打出时要执行的效果。
   * - `null` = 这张牌没有主动效果（致命牌、拆除卡、无效牌）
   * - 具体的纯函数实现由注册表按这个 ID 查（见 `pack.ts`）
   */
  readonly effect: EffectId | null;

  /**
   * **官方预设**里放几张。
   * 房主的游戏规则可以覆盖它（`docs/rules.md` §11）。
   */
  readonly defaultCount: number;
}

export const isLethal = (card: CardDef): boolean => card.traits.lethal !== undefined;

export const isDefusable = (card: CardDef): boolean => card.traits.lethal?.defusable === true;

export const isRescue = (card: CardDef): boolean => card.traits.rescue === true;

export const isInterrupt = (card: CardDef): boolean => card.traits.interrupt === true;

export const isPowerless = (card: CardDef): boolean => card.traits.powerless === true;

/** 省略 `playable` 时默认可以打出 */
export const isPlayable = (card: CardDef): boolean => card.traits.playable ?? true;
